import { useState, useEffect } from 'react';
import { Form, Button, Card, Container, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/auth.service';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import PageMeta from '../components/common/PageMeta';
import { setSampleAuthForLocalMode, setSampleDataSourcePreference, getSampleDataSourcePreference } from '../services/systems/sample-local/sampleLocalMode';
import { pauseSampleZipSession } from '../services/systems/sample-local/sampleZipFile';

function resolvePostLoginPath(searchParams: URLSearchParams): string {
  const next = searchParams.get('next');
  if (next && next.startsWith('/') && !next.startsWith('//')) {
    return next;
  }
  return '/dashboard';
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState<'password' | 'setup' | 'verify' | 'recovery'>('password');
  const [challenge, setChallenge] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [recoverySaved, setRecoverySaved] = useState(false);
  const [copied, setCopied] = useState('');
  const [busy, setBusy] = useState(false);
  const { login, isLoggingIn, isAuthenticated, isLoading, completeLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const goAfterLogin = () => {
    setSampleAuthForLocalMode(true);
    if (searchParams.get('sampleSource') === 'db') {
      setSampleDataSourcePreference('db');
    }
    // 既定 preference が db のときも Zip を pause（ゲスト作業中にログインしたケース）
    const preferDb = getSampleDataSourcePreference() === 'db';
    if (preferDb) {
      void pauseSampleZipSession().finally(() => {
        navigate(resolvePostLoginPath(searchParams), { replace: true });
      });
      return;
    }
    navigate(resolvePostLoginPath(searchParams), { replace: true });
  };

  useEffect(() => {
    if (step !== 'password') return;
    if (!isLoading && isAuthenticated) {
      goAfterLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回認証済みリダイレクトのみ
  }, [isAuthenticated, isLoading, step]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const result = await login({ email, password });
      if (result?.data?.token) {
        goAfterLogin();
        return;
      }
      if (result?.data?.totpSetupRequired && result.data.challenge) {
        setChallenge(result.data.challenge);
        setTotpSecret(result.data.secret || '');
        setQrDataUrl(result.data.qrDataUrl || null);
        setStep('setup');
        return;
      }
      if (result?.data?.totpRequired && result.data.challenge) {
        setChallenge(result.data.challenge);
        setStep('verify');
      }
    } catch (err: any) {
      showLoginError(err);
    }
  };

  const showLoginError = (err: any) => {
    if (err?.translationKey) {
      setError(t(err.translationKey, { defaultValue: err.message }));
      return;
    }
    const errorCode = err.response?.data?.error?.message;
    let errorMessage = t('auth.login_failed');
    if (errorCode === 'LOGIN_INVALID_CREDENTIALS') {
      errorMessage = t('auth.login_invalid_credentials', {
        defaultValue: 'メールアドレスまたはパスワードが正しくありません。入力内容をご確認ください。',
      });
    } else if (errorCode === 'LOGIN_ACCOUNT_INACTIVE') {
      errorMessage = t('auth.login_account_inactive', {
        defaultValue: 'このアカウントは現在無効になっています。管理者にお問い合わせください。',
      });
    } else if (errorCode === 'RATE_LIMIT_EXCEEDED' || errorCode === 'auth.rate_limit.login_exceeded') {
      errorMessage = t('auth.login_rate_limit_exceeded', {
        defaultValue: 'ログイン試行回数が上限に達しました。しばらく時間をおいてから再度お試しください。',
      });
    } else if (errorCode === 'TOTP_INVALID') {
      errorMessage = t('auth.totp_invalid', {
        defaultValue: 'コードが違います。認証アプリの6桁、または復旧コードを確認してください。',
      });
    } else if (errorCode === 'TOTP_CHALLENGE_EXPIRED') {
      errorMessage = t('auth.totp_expired', {
        defaultValue: '入力の期限が切れました。パスワードからやり直してください。',
      });
    } else if (errorCode === 'TOTP_ALREADY_ENABLED') {
      errorMessage = 'この設定は終わっています。パスワードから入り直して、6桁を入力してください。';
    } else if (errorCode) {
      errorMessage = t(`auth.${errorCode.toLowerCase()}`, { defaultValue: errorCode });
    } else if (err.message?.startsWith('api.error.')) {
      errorMessage = t(err.message, { defaultValue: err.message });
    } else if (err.message) {
      errorMessage = err.message;
    }
    setError(errorMessage);
  };

  const finishWithToken = async (token: string | undefined, codes?: string[]) => {
    if (!token) return;
    if (codes && codes.length > 0) {
      setPendingToken(token);
      setRecoveryCodes(codes);
      setStep('recovery');
      return;
    }
    localStorage.setItem('token', token);
    await completeLogin(token);
    goAfterLogin();
  };

  const copyText = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
    } catch {
      setCopied('');
    }
  };

  const handleTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result =
        step === 'setup'
          ? await authService.confirmTotp(challenge, totpCode)
          : await authService.verifyTotp(challenge, totpCode);
      await finishWithToken(result.data.token, result.data.recoveryCodes);
    } catch (err) {
      showLoginError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageMeta
        titleKey="login.title"
        descriptionKey="login.meta_description"
        path="login"
        llmsDescribedBy
        robots="noindex,follow"
      />
      <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #eff6ff 0%, #f8fafc 55%, #e2e8f0 100%)' }}>
      <Card style={{ width: '100%', maxWidth: step === 'password' ? '450px' : '520px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
        <Card.Body className="p-4">
          <Card.Title className="text-center mb-2" style={{ fontSize: '1.75rem', fontWeight: 600 }}>
            <i className={`fas ${step === 'password' ? 'fa-sign-in-alt' : step === 'recovery' ? 'fa-key' : 'fa-shield-alt'} me-2 text-primary`}></i>
            {step === 'password'
              ? t('auth.login')
              : step === 'setup'
                ? '認証アプリの設定'
                : step === 'verify'
                  ? '確認コード'
                  : '復旧コード'}
          </Card.Title>
          {step !== 'password' ? (
            <p className="text-center text-muted small mb-4">
              {step === 'setup'
                ? '管理者のログインは、パスワードのあとに6桁のコードが必要です。'
                : step === 'verify'
                  ? '認証アプリに表示されている6桁を入力します。'
                  : 'この画面を閉じると、コードは二度と表示されません。'}
            </p>
          ) : (
            <div className="mb-3" />
          )}

          {step === 'password' ? (
          <CollapsibleHelpSection
            titleKey="auth.how_to_use_title"
            descriptionKey="auth.how_to_use_desc"
            stepsKey={[
              'auth.how_to_use_step1',
              'auth.how_to_use_step2',
              'auth.how_to_use_step3',
            ]}
            storageKey="login"
          />
          ) : null}

          {error && (
            <Alert variant="danger" className="d-flex align-items-start mb-3">
              <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
              <div className="flex-grow-1">
                <Alert.Heading className="h6 mb-2">{t('auth.login_error_title', { defaultValue: 'ログインエラー' })}</Alert.Heading>
                <div className="mb-2">{error}</div>
                {step === 'password' ? (
                <div className="small text-muted">
                  {t('auth.login_error_hint', { 
                    defaultValue: '💡 ヒント: メールアドレスとパスワードを確認してください。パスワードを忘れた場合は「パスワードを忘れた方」からリセットできます。' 
                  })}
                </div>
                ) : null}
              </div>
            </Alert>
          )}
          <Form
            onSubmit={
              step === 'password' ? handleSubmit : step === 'recovery' ? (e) => e.preventDefault() : handleTotp
            }
          >
            {step === 'recovery' ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted">8個のうち1つで、認証アプリの代わりになります。</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => void copyText('recovery', recoveryCodes.join('\n'))}
                  >
                    {copied === 'recovery' ? 'コピーしました' : 'すべてコピー'}
                  </Button>
                </div>
                <div className="row g-2 mb-3">
                  {recoveryCodes.map((code) => (
                    <div className="col-6" key={code}>
                      <div
                        className="border rounded-3 py-2 text-center"
                        style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.04em', background: '#f8fafc' }}
                      >
                        {code}
                      </div>
                    </div>
                  ))}
                </div>
                <Form.Check
                  id="recovery-saved"
                  className="mb-3"
                  checked={recoverySaved}
                  onChange={(e) => setRecoverySaved(e.target.checked)}
                  label="パスワード管理ツールか紙に控えました"
                />
                <Button
                  variant="primary"
                  type="button"
                  className="w-100"
                  disabled={!recoverySaved}
                  onClick={() => {
                    void completeLogin(pendingToken).then(() => goAfterLogin());
                  }}
                >
                  ログインを続ける
                </Button>
              </>
            ) : step === 'password' ? (
              <>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="login-email">{t('auth.email')}</Form.Label>
              <Form.Control
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="login-password">{t('auth.password')}</Form.Label>
              <Form.Control
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </Form.Group>
            <Button
              variant="primary"
              type="submit"
              className="w-100"
              disabled={isLoggingIn}
            >
              {isLoggingIn ? t('common.loading') : t('auth.login')}
            </Button>
              </>
            ) : (
              <>
                {step === 'setup' ? (
                  <ol className="small text-muted ps-3 mb-3">
                    <li className="mb-1">「Google 認証システム」など、6桁を出すアプリを入れます。スマホのカメラアプリでは開けません。</li>
                    <li className="mb-1">そのアプリを開き、アカウントの追加から画像を読み取ります。読み取れないときは、画像の下の文字をアプリに手入力します。</li>
                    <li>アプリに出た6桁を、下の欄に入力します。</li>
                  </ol>
                ) : (
                  <p className="small text-muted mb-3">
                    アプリを開けないときは、設定のときに控えた復旧コードをそのまま入力できます。
                  </p>
                )}
                {step === 'setup' && qrDataUrl ? (
                  <div className="d-flex justify-content-center mb-3">
                    <div className="bg-white border rounded-4 p-3 shadow-sm">
                      <img src={qrDataUrl} alt="認証アプリで読み取る画像" width={220} height={220} />
                    </div>
                  </div>
                ) : null}
                {step === 'setup' && totpSecret ? (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <code
                      className="flex-grow-1 border rounded-3 px-3 py-2 text-break"
                      style={{ background: '#f8fafc', letterSpacing: '0.06em' }}
                    >
                      {totpSecret}
                    </code>
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => void copyText('secret', totpSecret)}
                    >
                      {copied === 'secret' ? 'コピーしました' : 'コピー'}
                    </Button>
                  </div>
                ) : null}
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="login-totp">6桁のコード</Form.Label>
                  <Form.Control
                    id="login-totp"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    autoComplete="one-time-code"
                    inputMode={step === 'verify' ? 'text' : 'numeric'}
                    placeholder={step === 'verify' ? '6桁、または復旧コード' : '000000'}
                    required
                    style={{
                      letterSpacing: step === 'setup' ? '0.45em' : '0.12em',
                      fontSize: '1.35rem',
                      textAlign: 'center',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  />
                </Form.Group>
                <Button variant="primary" type="submit" className="w-100" disabled={busy || totpCode.trim().length < 6}>
                  {busy ? t('common.loading') : '確認してログイン'}
                </Button>
              </>
            )}
          </Form>
          {step === 'password' ? (
          <div className="mt-3 text-center">
            <Button
              variant="link"
              onClick={() => navigate('/password-reset')}
              className="p-0 d-block mb-2"
            >
              <i className="fas fa-key me-1"></i>
              {t('auth.forgot_password')}
            </Button>
            <Button
              variant="link"
              onClick={() => navigate('/register')}
              className="p-0"
            >
              <i className="fas fa-user-plus me-1"></i>
              {t('auth.register')}
            </Button>
          </div>
          ) : step !== 'recovery' ? (
            <div className="mt-3 text-center">
              <Button
                variant="link"
                className="p-0"
                type="button"
                onClick={() => {
                  setStep('password');
                  setError('');
                  setTotpCode('');
                }}
              >
                パスワードの入力に戻る
              </Button>
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </Container>
    </>
  );
}

